import React, { useState, useRef, useEffect } from 'react';

const ColumnHeaderDropdown = ({
  field,
  onSort,
  onGroup,
  onSummarize,
  isSummarized = false,
  aggregationType = null,
  isGrouped = false,
  isColumnGrouped = false,
  sqlSortInfo = null,
  sqlSortLevelsCount = 0,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSummarizeSubmenu, setShowSummarizeSubmenu] = useState(false);
  const [submenuPosition, setSubmenuPosition] = useState('right');
  const dropdownRef = useRef(null);
  const submenuRef = useRef(null);
  const submenuTimeoutRef = useRef(null);

  // Helper function to convert aggregation type to readable label
  const getAggregationLabel = (type) => {
    const labels = {
      'sum': 'Sum',
      'avg': 'Average',
      'min': 'Minimum',
      'max': 'Maximum',
      'count': 'Count'
    };
    return labels[type] || type;
  };

  // Close dropdown when clicking outside (only while open; use click to avoid mousedown races)
  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowSummarizeSubmenu(false);
        if (submenuTimeoutRef.current) {
          clearTimeout(submenuTimeoutRef.current);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
    };
  }, [showDropdown]);

  const normalizedFieldType = (field.data_type || field.field_type || '').toLowerCase();
  const isNumericField = [
    'number',
    'integer',
    'decimal',
    'currency',
    'percent',
    'rollupsummary',
    'autonumber',
    'numeric',
    'float',
    'double',
    'real',
    'bigint',
    'smallint',
    'money'
  ].includes(normalizedFieldType);

  const handleSummarizeClick = (aggregation) => {
    // If the same aggregation is already selected, remove it
    if (isSummarized && aggregationType === aggregation) {
      onSummarize('remove_summarize', field, null);
    } else {
      onSummarize('summarize', field, aggregation);
    }
    setShowDropdown(false);
    setShowSummarizeSubmenu(false);
  };

  const handleSortClick = (direction) => {
    onSort('sort', field, direction);
    setShowDropdown(false);
  };

  const handleGroupClick = (type) => {
    onGroup('group', field, type);
    setShowDropdown(false);
  };

  return (
    <div className="column-header-dropdown" ref={dropdownRef}>
      <div
        className="header-cell-content"
        onClick={(event) => {
          event.stopPropagation();
          setShowDropdown((open) => !open);
        }}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>
            {field.field_label}
            {isSummarized && aggregationType && (
              <span style={{
                color: '#3b82f6',
                fontWeight: '600',
                marginLeft: '4px',
                fontSize: '0.9em'
              }}>
                ({getAggregationLabel(aggregationType)})
              </span>
            )}
          </span>
          {sqlSortInfo && (
            <span
              className="text-muted"
              title={`Report SQL sort — level ${sqlSortInfo.level} (${sqlSortInfo.direction})`}
              style={{ marginLeft: '2px', fontSize: '0.85em', whiteSpace: 'nowrap' }}
            >
              {sqlSortInfo.direction === 'desc' ? '\u2193' : '\u2191'}
              {sqlSortLevelsCount > 1 ? <sub style={{ fontSize: '0.75em' }}>{sqlSortInfo.level}</sub> : null}
            </span>
          )}
          {isGrouped && (
            <span className="grouped-indicator" title="Grouped by this field (rows)">
              ⊞
            </span>
          )}
          {isColumnGrouped && (
            <span className="grouped-indicator column-grouped-indicator" title="Grouped by this field (columns)">
              ⊟
            </span>
          )}
          {isSummarized && (
            <span className="summarized-indicator" title={`Summarized as ${aggregationType}`}>
              Σ
            </span>
          )}
        </div>
        <span className="dropdown-arrow">▼</span>
      </div>

      {showDropdown && (
        <div className="header-dropdown-menu">
          <div className="dropdown-item" onClick={() => handleSortClick('asc')}>
            <span className="dropdown-icon">↑</span>
            Sort ascending
          </div>
          <div className="dropdown-item" onClick={() => handleSortClick('desc')}>
            <span className="dropdown-icon">↓</span>
            Sort descending
          </div>
          {sqlSortInfo && (
            <div className="dropdown-item" onClick={() => { onSort('clear_sql_sort', field, null); setShowDropdown(false); }}>
              <span className="dropdown-icon">×</span>
              Remove this column from sort
            </div>
          )}
          {sqlSortLevelsCount > 0 && (
            <div className="dropdown-item" onClick={() => { onSort('clear_all_sql_sort', field, null); setShowDropdown(false); }}>
              <span className="dropdown-icon">×</span>
              Clear all sort levels
            </div>
          )}
          <div className={`dropdown-item ${isGrouped ? 'selected' : ''}`} onClick={() => handleGroupClick('rows')}>
            <span className="dropdown-icon">⊞</span>
            {isGrouped ? 'Remove Row Grouping' : 'Group Rows by This Field'}
          </div>
          <div className={`dropdown-item ${isColumnGrouped ? 'selected' : ''}`} onClick={() => handleGroupClick('columns')}>
            <span className="dropdown-icon">⊟</span>
            {isColumnGrouped ? 'Remove Column Grouping' : 'Group Columns by This Field'}
          </div>

          {isNumericField && !isGrouped && !isColumnGrouped && (
            <div
              className="dropdown-item has-submenu"
              onMouseEnter={(event) => {
                // Clear any pending timeout
                if (submenuTimeoutRef.current) {
                  clearTimeout(submenuTimeoutRef.current);
                  submenuTimeoutRef.current = null;
                }

                const menuRect = dropdownRef.current?.getBoundingClientRect();
                const submenuWidth = 180; // approximate width
                const viewportWidth = window.innerWidth;

                if (menuRect) {
                  const remainingSpace = viewportWidth - (menuRect.right + submenuWidth);
                  if (remainingSpace < 24) {
                    setSubmenuPosition('left');
                  } else {
                    setSubmenuPosition('right');
                  }
                } else {
                  setSubmenuPosition('right');
                }
                setShowSummarizeSubmenu(true);
              }}
              onMouseLeave={(event) => {
                const relatedTarget = event.relatedTarget;
                // Check if mouse is moving to submenu
                if (
                  submenuRef.current &&
                  relatedTarget &&
                  submenuRef.current.contains(relatedTarget)
                ) {
                  return;
                }
                // Add delay before closing to allow mouse to move to submenu
                submenuTimeoutRef.current = setTimeout(() => {
                  setShowSummarizeSubmenu(false);
                }, 200);
              }}
            >
              <span className="dropdown-icon">Σ</span>
              Summarize
              <span className="submenu-arrow">▶</span>

              {showSummarizeSubmenu && (
                <div
                  ref={submenuRef}
                  className={`submenu submenu-${submenuPosition}`}
                  onMouseLeave={(event) => {
                    const relatedTarget = event.relatedTarget;
                    if (
                      dropdownRef.current &&
                      relatedTarget &&
                      dropdownRef.current.contains(relatedTarget)
                    ) {
                      return;
                    }
                    // Add delay before closing
                    submenuTimeoutRef.current = setTimeout(() => {
                      setShowSummarizeSubmenu(false);
                    }, 200);
                  }}
                  onMouseEnter={() => {
                    // Clear any pending timeout when mouse enters submenu
                    if (submenuTimeoutRef.current) {
                      clearTimeout(submenuTimeoutRef.current);
                      submenuTimeoutRef.current = null;
                    }
                    setShowSummarizeSubmenu(true);
                  }}
                >
                  <div className="submenu-item" onClick={() => handleSummarizeClick('sum')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'sum' ? '✓' : ''}
                    </span>
                    Sum
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('avg')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'avg' ? '✓' : ''}
                    </span>
                    Average
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('max')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'max' ? '✓' : ''}
                    </span>
                    Max
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('min')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'min' ? '✓' : ''}
                    </span>
                    Min
                  </div>
                  <div className="submenu-item" onClick={() => handleSummarizeClick('count')}>
                    <span className="submenu-checkbox">
                      {isSummarized && aggregationType === 'count' ? '✓' : ''}
                    </span>
                    Count
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColumnHeaderDropdown;
